/**
 * AI API - Cloud Functions for 360 Image Generation and Editing
 * Uses Nano Banana Pro (Google Gemini) via Firebase Cloud Functions
 */

import { httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { functions } from '../firebase/config';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('AI');

// Helper to extract error message from unknown error
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

// Types
export type AIOperationType =
  | 'generate'
  | 'edit'
  | 'fix-nadir'
  | 'fix-zenith'
  | 'virtual-staging'
  | 'weather'
  | 'reskin';

export type StagingStyle = 'modern' | 'traditional' | 'minimalist' | 'scandinavian' | 'industrial';
export type WeatherEffect = 'snow' | 'rain' | 'fog' | 'sunset' | 'night' | 'autumn';

export interface AIJobStatus {
  id: string;
  userId: string;
  sequenceId: string;
  stageId: string;           // Unique stage identifier (stable across reordering)
  stageIndex?: number;       // Legacy: array index (may change on reorder)
  operation: AIOperationType;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  prompt?: string;
  inputUrl?: string;
  outputUrl?: string;
  originalImageUrl?: string; // The image that was there before AI replaced it
  error?: string;
  model?: 'pro' | 'flash';   // Which model was used
  createdAt: Timestamp;
  updatedAt: Timestamp;
  completedAt?: Timestamp;
}

export type AIModel = 'pro' | 'flash';

export interface AIResult {
  success: boolean;
  jobId: string;
  outputUrl?: string;
  model?: string; // The model that was actually used
  versionId?: string; // Version ID for the created version
  versionNumber?: number; // Version number for display
}

// Cloud Function references with extended timeout (9 minutes = 540000ms)
const AI_TIMEOUT = 540000; // 9 minutes in milliseconds

// All AI functions now require stageId (unique identifier) instead of stageIndex (array position)
// This ensures version history stays with the stage even when stages are reordered

const generate360Fn = httpsCallable<
  { prompt: string; sequenceId: string; stageId: string; stageIndex?: number; model?: AIModel },
  AIResult
>(functions, 'generate360', { timeout: AI_TIMEOUT });

const edit360Fn = httpsCallable<
  { imageUrl: string; prompt: string; sequenceId: string; stageId: string; stageIndex?: number; originalImageUrl?: string; useCubeMap?: boolean; model?: AIModel },
  AIResult
>(functions, 'edit360', { timeout: AI_TIMEOUT });

const fixNadirFn = httpsCallable<
  { imageUrl: string; sequenceId: string; stageId: string; stageIndex?: number; originalImageUrl?: string; model?: AIModel },
  AIResult
>(functions, 'fixNadir', { timeout: AI_TIMEOUT });

const fixZenithFn = httpsCallable<
  { imageUrl: string; sequenceId: string; stageId: string; stageIndex?: number; originalImageUrl?: string; model?: AIModel },
  AIResult
>(functions, 'fixZenith', { timeout: AI_TIMEOUT });

const virtualStagingFn = httpsCallable<
  { imageUrl: string; style: StagingStyle; sequenceId: string; stageId: string; stageIndex?: number; originalImageUrl?: string; model?: AIModel },
  AIResult
>(functions, 'virtualStaging', { timeout: AI_TIMEOUT });

const addWeatherFn = httpsCallable<
  { imageUrl: string; weather: WeatherEffect; sequenceId: string; stageId: string; stageIndex?: number; originalImageUrl?: string; model?: AIModel },
  AIResult
>(functions, 'addWeather', { timeout: AI_TIMEOUT });

const reskin360Fn = httpsCallable<
  { imageUrl: string; style: string; sequenceId: string; stageId: string; stageIndex?: number; model?: AIModel },
  AIResult
>(functions, 'reskin360', { timeout: AI_TIMEOUT });

const getAIJobStatusFn = httpsCallable<{ jobId: string }, AIJobStatus>(
  functions,
  'getAIJobStatus'
);

const listAIJobsFn = httpsCallable<
  { sequenceId?: string; limit?: number },
  { jobs: AIJobStatus[] }
>(functions, 'listAIJobs');

// Per-model usage stats
export interface ModelUsageStats {
  used: number;
  limit: number;
  remaining: number;
}

// AI Usage Stats - now tracks per-model usage
export interface AIUsageStats {
  tier: 'free' | 'basic' | 'pro' | 'admin';
  pro: ModelUsageStats;
  flash: ModelUsageStats;
  totalAllTime: number;
  canUseAI: boolean;
}

const getAIUsageFn = httpsCallable<void, AIUsageStats>(
  functions,
  'getAIUsage'
);

// BYOK (Bring Your Own Key) Cloud Function references
export interface PersonalKeyStatus {
  hasKey: boolean;
  keyPreview?: string;  // Last 4 characters for display
  lastValidated?: Timestamp;
}

const setPersonalGeminiKeyFn = httpsCallable<
  { apiKey: string },
  { success: boolean; keyPreview: string }
>(functions, 'setPersonalGeminiKey');

const removePersonalGeminiKeyFn = httpsCallable<
  void,
  { success: boolean }
>(functions, 'removePersonalGeminiKey');

const checkPersonalGeminiKeyFn = httpsCallable<
  void,
  PersonalKeyStatus
>(functions, 'checkPersonalGeminiKey');

/**
 * Generate a 360 image from a text prompt
 *
 * @param prompt - Text description of the 360 environment
 * @param sequenceId - Sequence to add the image to
 * @param stageId - Unique stage identifier (stable across reordering)
 * @param stageIndex - Stage array index (for legacy compatibility)
 * @param model - Which model to use: 'pro' or 'flash'
 * @returns Job result with output URL and model used
 */
export async function generate360(
  prompt: string,
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  model: AIModel = 'pro'
): Promise<AIResult> {
  try {
    logger.info(` Generating 360 (model: ${model}, stage: ${stageId}):`, prompt.substring(0, 50) + '...');
    const result = await generate360Fn({ prompt, sequenceId, stageId, stageIndex, model });
    return result.data;
  } catch (error) {
    logger.error(' Generate 360 failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to generate 360 image');
  }
}

// Model display names for UI
export const AI_MODEL_OPTIONS: { value: AIModel; label: string; description: string }[] = [
  { value: 'pro', label: 'Nano Banana Pro (Gemini 3 Pro)', description: 'Best quality, 4K output' },
  { value: 'flash', label: 'Nano Banana (Gemini 2.5 Flash)', description: 'Faster, more reliable' },
];

/**
 * Edit a 360 image with natural language
 *
 * @param imageUrl - URL of the 360 image to edit
 * @param prompt - Natural language edit instruction
 * @param sequenceId - Sequence ID
 * @param stageId - Unique stage identifier
 * @param stageIndex - Stage array index (optional, for legacy)
 * @param originalImageUrl - The image currently in the stage (for version tracking)
 * @param useCubeMap - Use cube-map conversion (default: false - direct editing works better for full image)
 * @param model - Which model to use: 'pro' or 'flash'
 * @returns Job result with output URL
 */
export async function edit360(
  imageUrl: string,
  prompt: string,
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  originalImageUrl?: string,
  useCubeMap: boolean = false,
  model: AIModel = 'flash'
): Promise<AIResult> {
  try {
    logger.info(` Editing 360 (stage: ${stageId}):`, prompt.substring(0, 50) + '...');
    const result = await edit360Fn({ imageUrl, prompt, sequenceId, stageId, stageIndex, originalImageUrl, useCubeMap, model });
    return result.data;
  } catch (error) {
    logger.error(' Edit 360 failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to edit 360 image');
  }
}

/**
 * Fix the nadir (bottom) of a 360 image - removes tripod shadows
 */
export async function fixNadir(
  imageUrl: string,
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  originalImageUrl?: string,
  model: AIModel = 'flash'
): Promise<AIResult> {
  try {
    logger.info(` Fixing nadir (stage: ${stageId})...`);
    const result = await fixNadirFn({ imageUrl, sequenceId, stageId, stageIndex, originalImageUrl, model });
    return result.data;
  } catch (error) {
    logger.error(' Fix nadir failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fix nadir');
  }
}

/**
 * Fix the zenith (top) of a 360 image - fixes stitching issues
 */
export async function fixZenith(
  imageUrl: string,
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  originalImageUrl?: string,
  model: AIModel = 'flash'
): Promise<AIResult> {
  try {
    logger.info(` Fixing zenith (stage: ${stageId})...`);
    const result = await fixZenithFn({ imageUrl, sequenceId, stageId, stageIndex, originalImageUrl, model });
    return result.data;
  } catch (error) {
    logger.error(' Fix zenith failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to fix zenith');
  }
}

/**
 * Apply virtual staging to a 360 image
 */
export async function virtualStaging(
  imageUrl: string,
  style: StagingStyle,
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  originalImageUrl?: string,
  model: AIModel = 'flash'
): Promise<AIResult> {
  try {
    logger.info(` Virtual staging (stage: ${stageId}) with style: ${style}`);
    const result = await virtualStagingFn({ imageUrl, style, sequenceId, stageId, stageIndex, originalImageUrl, model });
    return result.data;
  } catch (error) {
    logger.error(' Virtual staging failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to apply virtual staging');
  }
}

/**
 * Add weather effects to a 360 image
 */
export async function addWeather(
  imageUrl: string,
  weather: WeatherEffect,
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  originalImageUrl?: string,
  model: AIModel = 'flash'
): Promise<AIResult> {
  try {
    logger.info(` Adding weather effect (stage: ${stageId}): ${weather}`);
    const result = await addWeatherFn({ imageUrl, weather, sequenceId, stageId, stageIndex, originalImageUrl, model });
    return result.data;
  } catch (error) {
    logger.error(' Add weather failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to add weather effect');
  }
}

/**
 * Reskin/style transfer a 360 image
 */
export async function reskin360(
  imageUrl: string,
  style: string,
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  model: AIModel = 'flash'
): Promise<AIResult> {
  try {
    logger.info(` Reskinning (stage: ${stageId}) with style: ${style.substring(0, 30)}...`);
    const result = await reskin360Fn({ imageUrl, style, sequenceId, stageId, stageIndex, model });
    return result.data;
  } catch (error) {
    logger.error(' Reskin failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to reskin image');
  }
}

/**
 * Get status of an AI job
 */
export async function getAIJobStatus(jobId: string): Promise<AIJobStatus> {
  try {
    const result = await getAIJobStatusFn({ jobId });
    return result.data;
  } catch (error) {
    logger.error(' Get job status failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to get job status');
  }
}

/**
 * List AI jobs for current user
 */
export async function listAIJobs(
  sequenceId?: string,
  limit: number = 20
): Promise<AIJobStatus[]> {
  try {
    const result = await listAIJobsFn({ sequenceId, limit });
    return result.data.jobs;
  } catch (error) {
    logger.error(' List jobs failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to list AI jobs');
  }
}

/**
 * Get AI usage stats for the current user
 * Returns monthly usage, limits, and whether AI is available
 */
export async function getAIUsage(): Promise<AIUsageStats> {
  try {
    const result = await getAIUsageFn();
    return result.data;
  } catch (error) {
    logger.error(' Get usage failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to get AI usage stats');
  }
}

// ============================================================================
// BYOK (BRING YOUR OWN KEY) API
// ============================================================================

/**
 * Set personal Gemini API key for BYOK
 * This allows users to use their own API key to bypass platform quotas
 * The key is validated with a test API call before saving
 *
 * @param apiKey - User's personal Gemini API key
 * @returns Success status and key preview (last 4 chars)
 */
export async function setPersonalGeminiKey(apiKey: string): Promise<{ success: boolean; keyPreview: string }> {
  try {
    logger.info(' Setting personal Gemini API key...');
    const result = await setPersonalGeminiKeyFn({ apiKey });
    logger.info(' Personal API key saved successfully');
    return result.data;
  } catch (error) {
    logger.error(' Set personal key failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to save API key');
  }
}

/**
 * Remove personal Gemini API key
 * After removal, the user will revert to using platform quota
 */
export async function removePersonalGeminiKey(): Promise<{ success: boolean }> {
  try {
    logger.info(' Removing personal Gemini API key...');
    const result = await removePersonalGeminiKeyFn();
    logger.info(' Personal API key removed');
    return result.data;
  } catch (error) {
    logger.error(' Remove personal key failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to remove API key');
  }
}

/**
 * Check if user has a personal Gemini API key configured
 * Returns key status and a preview of the last 4 characters
 */
export async function checkPersonalGeminiKey(): Promise<PersonalKeyStatus> {
  try {
    const result = await checkPersonalGeminiKeyFn();
    return result.data;
  } catch (error) {
    logger.error(' Check personal key failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to check API key status');
  }
}

/**
 * Poll for job completion with timeout
 */
export async function waitForJobCompletion(
  jobId: string,
  maxWaitMs: number = 300000, // 5 minutes default
  pollIntervalMs: number = 2000
): Promise<AIJobStatus> {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    const job = await getAIJobStatus(jobId);

    if (job.status === 'completed') {
      return job;
    }

    if (job.status === 'failed') {
      throw new Error(job.error || 'AI job failed');
    }

    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
  }

  throw new Error('AI job timed out');
}

// Preset style suggestions for the UI
export const STAGING_STYLES: { value: StagingStyle; label: string; description: string }[] = [
  { value: 'modern', label: 'Modern', description: 'Clean lines, neutral colors, sleek finishes' },
  { value: 'traditional', label: 'Traditional', description: 'Warm wood tones, ornate details, rich fabrics' },
  { value: 'minimalist', label: 'Minimalist', description: 'Few items, open space, simple colors' },
  { value: 'scandinavian', label: 'Scandinavian', description: 'Light wood, white colors, cozy textiles' },
  { value: 'industrial', label: 'Industrial', description: 'Exposed materials, metal and wood, urban aesthetic' },
];

export const WEATHER_EFFECTS: { value: WeatherEffect; label: string; description: string }[] = [
  { value: 'snow', label: 'Snow', description: 'Falling snow, frost, winter atmosphere' },
  { value: 'rain', label: 'Rain', description: 'Rain effects, wet surfaces, puddles' },
  { value: 'fog', label: 'Fog', description: 'Atmospheric fog or mist' },
  { value: 'sunset', label: 'Sunset', description: 'Golden hour with warm orange and pink tones' },
  { value: 'night', label: 'Night', description: 'Nighttime with moonlight and artificial lighting' },
  { value: 'autumn', label: 'Autumn', description: 'Fall colors, fallen leaves, warm atmosphere' },
];

export const RESKIN_PRESETS: { value: string; label: string }[] = [
  { value: 'cyberpunk neon city with holographic advertisements', label: 'Cyberpunk' },
  { value: 'fantasy medieval castle interior with torches and tapestries', label: 'Fantasy Medieval' },
  { value: 'futuristic space station with clean white surfaces and blue lighting', label: 'Sci-Fi Space' },
  { value: 'underwater coral reef with fish and sunlight rays', label: 'Underwater' },
  { value: 'ancient Egyptian temple with hieroglyphics and golden decorations', label: 'Ancient Egypt' },
  { value: 'Japanese zen garden with cherry blossoms and wooden architecture', label: 'Japanese Zen' },
  { value: 'post-apocalyptic abandoned building with overgrown plants', label: 'Post-Apocalyptic' },
  { value: 'steampunk victorian interior with brass gears and steam pipes', label: 'Steampunk' },
];

// ============================================================================
// VERSION HISTORY API
// ============================================================================

export type VersionSource =
  | 'upload'
  | 'ai-generate'
  | 'ai-edit'
  | 'ai-fix-nadir'
  | 'ai-fix-zenith'
  | 'ai-staging'
  | 'ai-weather'
  | 'ai-reskin';

export interface StageVersion {
  id: string;
  sequenceId: string;
  stageId: string;             // Unique stage identifier (stable across reordering)
  stageIndex?: number;         // Legacy: array index
  imageUrl: string;
  thumbnailUrl?: string;
  versionNumber: number;
  source: VersionSource;
  isActive: boolean;
  originalImageUrl?: string;   // The image that was in the stage before this version
  parentVersionId?: string;
  parentImageUrl?: string;
  aiJobId?: string;
  prompt?: string;
  operation?: string;
  createdBy: string;
  createdAt: Timestamp;
  label?: string;
  notes?: string;
}

export interface StageHistorySummary {
  stageId: string;             // Unique stage identifier
  stageIndex?: number;         // Legacy: array index
  totalVersions: number;
  activeVersionId: string | null;
  activeVersionNumber: number | null;
  latestVersionNumber: number;
  hasAIGenerations: boolean;
}

// Version History Cloud Function references - now use stageId instead of stageIndex
const getStageVersionHistoryFn = httpsCallable<
  { sequenceId: string; stageId: string; stageIndex?: number; limit?: number },
  { versions: StageVersion[]; summary: StageHistorySummary | null }
>(functions, 'getStageVersionHistory');

const getStageVersionFn = httpsCallable<
  { sequenceId: string; stageId: string; stageIndex?: number; versionId: string },
  { version: StageVersion | null }
>(functions, 'getStageVersion');

const revertToVersionFn = httpsCallable<
  { sequenceId: string; stageId: string; stageIndex?: number; versionId: string },
  { success: boolean; version: StageVersion }
>(functions, 'revertToVersion');

const getVersionChainFn = httpsCallable<
  { sequenceId: string; stageId: string; stageIndex?: number; versionId: string; maxDepth?: number },
  { chain: StageVersion[] }
>(functions, 'getVersionChain');

const createUploadVersionFn = httpsCallable<
  { sequenceId: string; stageId: string; stageIndex?: number; imageUrl: string; thumbnailUrl?: string; originalImageUrl?: string; setAsActive?: boolean },
  { success: boolean; version: StageVersion }
>(functions, 'createUploadVersion');

const applyVersionFn = httpsCallable<
  { sequenceId: string; stageId: string; stageIndex?: number; versionId: string; updateAllDevices?: boolean },
  { success: boolean; version: StageVersion; imageUrl: string; allDevicesUpdated?: boolean }
>(functions, 'applyVersion');

const deleteStageVersionFn = httpsCallable<
  { sequenceId: string; stageId: string; versionId: string },
  { success: boolean }
>(functions, 'deleteStageVersion');

const duplicateStageVersionsFn = httpsCallable<
  { sequenceId: string; sourceStageId: string; targetStageId: string },
  { success: boolean; versionsCopied: number }
>(functions, 'duplicateStageVersions');

/**
 * Get version history for a stage
 */
export async function getStageVersionHistory(
  sequenceId: string,
  stageId: string,
  stageIndex?: number,
  limit: number = 50
): Promise<{ versions: StageVersion[]; summary: StageHistorySummary | null }> {
  try {
    logger.info(` Getting version history for stage ${stageId}`);
    const result = await getStageVersionHistoryFn({ sequenceId, stageId, stageIndex, limit });
    return result.data;
  } catch (error) {
    logger.error(' Get version history failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to get version history');
  }
}

/**
 * Get a specific version
 */
export async function getStageVersion(
  sequenceId: string,
  stageId: string,
  versionId: string,
  stageIndex?: number
): Promise<StageVersion | null> {
  try {
    const result = await getStageVersionFn({ sequenceId, stageId, stageIndex, versionId });
    return result.data.version;
  } catch (error) {
    logger.error(' Get version failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to get version');
  }
}

/**
 * Revert to a previous version (set it as active)
 */
export async function revertToVersion(
  sequenceId: string,
  stageId: string,
  versionId: string,
  stageIndex?: number
): Promise<StageVersion> {
  try {
    logger.info(` Reverting stage ${stageId} to version ${versionId}`);
    const result = await revertToVersionFn({ sequenceId, stageId, stageIndex, versionId });
    return result.data.version;
  } catch (error) {
    logger.error(' Revert failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to revert to version');
  }
}

/**
 * Get the edit chain for a version (trace back through parents)
 */
export async function getVersionChain(
  sequenceId: string,
  stageId: string,
  versionId: string,
  maxDepth: number = 10,
  stageIndex?: number
): Promise<StageVersion[]> {
  try {
    const result = await getVersionChainFn({ sequenceId, stageId, stageIndex, versionId, maxDepth });
    return result.data.chain;
  } catch (error) {
    logger.error(' Get version chain failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to get version chain');
  }
}

/**
 * Create a version entry for an uploaded image
 * Call this after uploading a skybox to track it in version history
 */
export async function createUploadVersion(
  sequenceId: string,
  stageId: string,
  imageUrl: string,
  stageIndex?: number,
  thumbnailUrl?: string,
  originalImageUrl?: string,
  setAsActive: boolean = true
): Promise<StageVersion> {
  try {
    logger.info(` Creating upload version for stage ${stageId}`);
    const result = await createUploadVersionFn({
      sequenceId,
      stageId,
      stageIndex,
      imageUrl,
      thumbnailUrl,
      originalImageUrl,
      setAsActive
    });
    return result.data.version;
  } catch (error) {
    logger.error(' Create upload version failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to create upload version');
  }
}

/**
 * Apply a version to the stage (make it the current skybox)
 * This updates all device URLs (desktop, mobile, fallback) to the version's image
 */
export async function applyVersion(
  sequenceId: string,
  stageId: string,
  versionId: string,
  stageIndex?: number,
  updateAllDevices: boolean = true
): Promise<{ version: StageVersion; imageUrl: string; allDevicesUpdated?: boolean }> {
  try {
    logger.info(` Applying version ${versionId} to stage ${stageId}`);
    const result = await applyVersionFn({ sequenceId, stageId, stageIndex, versionId, updateAllDevices });
    return {
      version: result.data.version,
      imageUrl: result.data.imageUrl,
      allDevicesUpdated: result.data.allDevicesUpdated
    };
  } catch (error) {
    logger.error(' Apply version failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to apply version');
  }
}

/**
 * Delete a version from stage history
 * Cannot delete the currently active version
 */
export async function deleteStageVersion(
  sequenceId: string,
  stageId: string,
  versionId: string
): Promise<{ success: boolean }> {
  try {
    logger.info(` Deleting version ${versionId} from stage ${stageId}`);
    const result = await deleteStageVersionFn({ sequenceId, stageId, versionId });
    return result.data;
  } catch (error) {
    logger.error(' Delete version failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to delete version');
  }
}

/**
 * Duplicate version history from one stage to another
 * Used when duplicating a stage in the editor
 */
export async function duplicateStageVersions(
  sequenceId: string,
  sourceStageId: string,
  targetStageId: string
): Promise<{ success: boolean; versionsCopied: number }> {
  try {
    logger.info(` Duplicating versions from stage ${sourceStageId} to ${targetStageId}`);
    const result = await duplicateStageVersionsFn({ sequenceId, sourceStageId, targetStageId });
    return result.data;
  } catch (error) {
    logger.error(' Duplicate versions failed:', error);
    throw new Error(getErrorMessage(error) || 'Failed to duplicate versions');
  }
}

/**
 * Get friendly label for version source
 */
export function getVersionSourceLabel(source: VersionSource): string {
  const labels: Record<VersionSource, string> = {
    'upload': 'Uploaded',
    'ai-generate': 'AI Generated',
    'ai-edit': 'AI Edited',
    'ai-fix-nadir': 'Fixed Nadir',
    'ai-fix-zenith': 'Fixed Zenith',
    'ai-staging': 'Virtual Staging',
    'ai-weather': 'Weather Effect',
    'ai-reskin': 'Style Transfer',
  };
  return labels[source] || source;
}
